import {useCallback, useMemo} from 'react';

import {
  GenericTreeNode,
  getAllLeaveKeys,
  getSelectedLeaves,
  getTreeNodeMap,
  groupSelectedKeys,
  ungroupSelectedKeys,
} from 'utils/treeUtils';

import {TreeBasicNode, TreeKey} from 'components/Tree/TreeTypes';

type TreeSelectionProps<K extends TreeKey, T extends TreeBasicNode<K>> = {
  treeData: GenericTreeNode<T>[];
  /**
   * Shouldn't contain disabled or hidden nodes to support https://ennabl.youtrack.cloud/issue/FE-4725 behavior
   */
  filteredTreeData?: GenericTreeNode<T>[];
  selected: K[];
  setSelected: (keys: K[]) => void;
};

export function useTreeSelection<K extends TreeKey, T extends TreeBasicNode<K>>({
  treeData,
  filteredTreeData = treeData,
  selected,
  setSelected,
}: TreeSelectionProps<K, T>) {
  return useMemo(() => {
    // `filteredTreeData` is built by filtering disabled/hidden leaves out of the
    // tree, which can leave parent nodes whose children were all removed. Those
    // emptied parents look like leaves to `getAllLeaveKeys`, so restrict the
    // result to keys that are genuine leaves of the full tree.
    const realLeafKeys = new Set(getAllLeaveKeys<K, T>(treeData));
    const filteredLeafKeys = getAllLeaveKeys<K, T>(filteredTreeData).filter(key => realLeafKeys.has(key));

    const selectedLeafKeys = getSelectedLeaves<K, T>(treeData, new Set(selected));
    const outsideOfFilteredTreeSelected = selectedLeafKeys.filter(item => !filteredLeafKeys.includes(item));

    const onSelectAll = () => {
      setSelected([...outsideOfFilteredTreeSelected, ...filteredLeafKeys]);
    };

    const onInverseSelection = () => {
      const result = new Set(filteredLeafKeys);

      filteredLeafKeys.forEach(key => {
        if (selectedLeafKeys.includes(key)) {
          result.delete(key);
        }
      });

      setSelected([...outsideOfFilteredTreeSelected, ...Array.from(result)]);
    };

    const onCheck = (newSelected: K[]) => {
      const filteredNewSelected = newSelected.filter(item => filteredLeafKeys.includes(item));
      setSelected([...outsideOfFilteredTreeSelected, ...filteredNewSelected]);
    };

    const onSelectNone = () => {
      setSelected(outsideOfFilteredTreeSelected);
      return outsideOfFilteredTreeSelected;
    };

    return {
      onSelectNone,
      onInverseSelection,
      onSelectAll,
      selectionCount: selected.length,
      isSelectAllDisabled: filteredLeafKeys.length === 0,
      unselectItem: (key: K) => setSelected(selected.filter(item => item !== key)),
      onCheck,
    };
  }, [filteredTreeData, treeData, selected, setSelected]);
}

export function useTreeGroupedSelection<K extends string | number, T extends {key: K}>(
  treeData: GenericTreeNode<T>[],
  selected: K[],
  setSelected: (keys: K[]) => void
) {
  const treeMap = useMemo(() => getTreeNodeMap(treeData), [treeData]);
  const groupedSelected = useMemo(() => Array.from(groupSelectedKeys(treeData, selected)), [treeData, selected]);
  const selectedItems = useMemo(
    () =>
      groupedSelected.flatMap(key => {
        const item = treeMap.get(key);
        return item ? [item] : [];
      }),
    [groupedSelected, treeMap]
  );
  const unselectItem = useCallback(
    (key: K) => {
      const newGroupedSelected = groupedSelected.filter(item => item !== key);
      const newSelected = ungroupSelectedKeys(treeData, newGroupedSelected);
      setSelected(Array.from(newSelected));
    },
    [groupedSelected, setSelected, treeData]
  );

  return {
    selectedItems,
    unselectItem,
  };
}
